import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { PageSkeleton } from '../ui/Skeleton';
import { EmptyState } from '../ui/EmptyState';
import { StatusBadge, Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { IconQuotes } from '../icons';
import { money } from '../money';

/**
 * The customer's decision, on the customer's screen.
 *
 * The invoice page let somebody see what they owed. This is the one that makes a shop money:
 * until now the only way to say yes was to reply to an email and wait for the office to
 * notice and press a button, so a quote could sit "sent" for a week because nobody read
 * their inbox on a Saturday.
 *
 * Two buttons, and they only exist while the question is open. An approved quote shows that
 * it is approved and nothing to press — a customer who changes their mind rings the shop,
 * because by then parts may be on order, and a button that quietly un-orders them would be
 * a lie about what this software can do.
 */
export function PublicQuotePage() {
  const { token } = useParams({ from: '/q/$token' });
  const trpc = useTRPC();
  const qc = useQueryClient();
  const q = useQuery(trpc.public.quote.queryOptions({ token }));
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');

  const settled = () => void qc.invalidateQueries();
  const approve = useMutation(
    trpc.public.approveQuote.mutationOptions({ onSuccess: settled }),
  );
  const decline = useMutation(
    trpc.public.declineQuote.mutationOptions({
      onSuccess: () => {
        setDeclining(false);
        settled();
      },
    }),
  );

  if (q.isPending) return <PageSkeleton />;
  if (q.isError || !q.data) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <EmptyState
          icon={IconQuotes}
          title="This link doesn't work"
          description="It may have been withdrawn, or the address may be incomplete. Reply to the email it came from and whoever sent it can help."
        />
      </div>
    );
  }

  const quote = q.data;
  const busy = approve.isPending || decline.isPending;
  const error = approve.error ?? decline.error;

  return (
    <main className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
      <header className="mb-6">
        <p className="text-sm text-muted-foreground">{quote.shopName}</p>
        <h1 className="mt-1 text-2xl font-semibold">
          {quote.title ?? `Quote ${quote.number}`}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <StatusBadge
            tone={
              quote.status === 'approved'
                ? 'success'
                : quote.status === 'sent'
                  ? 'warn'
                  : 'neutral'
            }
          >
            {quote.statusLabel}
          </StatusBadge>
          <span className="text-sm text-muted-foreground">{quote.number}</span>
          {quote.validUntil && quote.canDecide ? (
            <span className="text-sm text-muted-foreground">
              Holds until {quote.validUntil}
            </span>
          ) : null}
        </div>
      </header>

      {/* The server's words, not a generic failure — "this price has expired" is something
          the customer can act on, and it is the shop's sentence. */}
      {error ? <Notice tone="danger">{error.message}</Notice> : null}

      {quote.status === 'expired' ? (
        <Notice tone="warn">
          This price has expired. Get in touch with {quote.shopName} and they&apos;ll send
          you an up-to-date one.
        </Notice>
      ) : null}
      {quote.status === 'superseded' ? (
        <Notice tone="neutral">
          A newer version of this quote has been sent — please use that one.
        </Notice>
      ) : null}
      {quote.status === 'declined' ? (
        <Notice tone="neutral">
          You turned this down
          {quote.declinedReason ? ` — "${quote.declinedReason}"` : ''}. Get in touch if
          that isn&apos;t what you meant.
        </Notice>
      ) : null}

      <section className="rounded-[var(--radius)] border border-border bg-surface">
        <div className="h-scroll">
          <table className="w-full min-w-[28rem] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-2 font-semibold">What we&apos;d do</th>
                <th className="px-4 py-2 text-right font-semibold">Qty</th>
                <th className="px-4 py-2 text-right font-semibold">Each</th>
                <th className="px-4 py-2 text-right font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {quote.lines.map((l, i) => (
                <tr key={i} className="border-b border-border last:border-b-0">
                  <td className="px-4 py-2.5">
                    <p className="font-medium">{l.name}</p>
                    {l.description ? (
                      <p className="text-xs text-muted-foreground">{l.description}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{l.qty}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {money(l.unitPriceCents)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {money(l.totalCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <dl className="space-y-1 border-t border-border px-4 py-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Subtotal</dt>
            <dd className="tabular-nums">{money(quote.subtotalCents)}</dd>
          </div>
          {quote.taxCents > 0 ? (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Tax</dt>
              <dd className="tabular-nums">{money(quote.taxCents)}</dd>
            </div>
          ) : null}
          <div className="flex justify-between text-base font-semibold">
            <dt>Total</dt>
            <dd className="tabular-nums">{money(quote.totalCents)}</dd>
          </div>
        </dl>

        {quote.depositRequiredCents ? (
          <p className="border-t border-border px-4 py-3 text-sm text-muted-foreground">
            A deposit of{' '}
            <strong className="text-text">{money(quote.depositRequiredCents)}</strong> is
            needed before the work starts. {quote.shopName} will be in touch about it.
          </p>
        ) : null}
      </section>

      {quote.termsText ? (
        <section className="mt-4 rounded-[var(--radius)] border border-border bg-surface p-4">
          <h2 className="text-sm font-semibold">Terms</h2>
          <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
            {quote.termsText}
          </p>
        </section>
      ) : null}

      {quote.canDecide ? (
        <section className="mt-4 rounded-[var(--radius)] border border-border bg-surface p-4">
          <h2 className="text-sm font-semibold">Would you like us to go ahead?</h2>

          {declining ? (
            <form
              className="mt-3 space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                decline.mutate({ token, reason: reason.trim() || undefined });
              }}
            >
              <label className="block text-sm" htmlFor="decline-reason">
                Anything you&apos;d like them to know? (optional)
              </label>
              <textarea
                id="decline-reason"
                className="min-h-20 w-full rounded-[var(--radius-sm)] border border-border bg-surface px-3 py-2 text-sm"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                placeholder="Went with someone else, timing doesn't work, price…"
              />
              <div className="flex flex-wrap gap-2">
                <Button type="submit" disabled={busy}>
                  {decline.isPending ? 'Sending…' : 'Send'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setDeclining(false)}
                  disabled={busy}
                >
                  Back
                </Button>
              </div>
            </form>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button onClick={() => approve.mutate({ token })} disabled={busy}>
                {approve.isPending ? 'Approving…' : 'Yes, go ahead'}
              </Button>
              <Button variant="ghost" onClick={() => setDeclining(true)} disabled={busy}>
                No thanks
              </Button>
            </div>
          )}
        </section>
      ) : null}

      {quote.status === 'approved' ? (
        <Notice tone="success">
          Thank you — {quote.shopName} has been told and will be in touch to arrange the
          work.
        </Notice>
      ) : null}

      <footer className="mt-6 text-xs text-muted-foreground">
        <p className="font-medium">{quote.shopName}</p>
        {quote.shopAddress ? (
          <p className="whitespace-pre-line">{quote.shopAddress}</p>
        ) : null}
      </footer>
    </main>
  );
}
